import React, { useEffect, useState } from 'react';

// material-ui
import { Collapse, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { Add, ArticleOutlined, ExpandLess, ExpandMore, Folder, FolderOpen, LockPersonOutlined } from '@mui/icons-material/';

import { TreeFolder } from '../dataObjects/KnowledgeTypes';

interface Props {
    root: TreeFolder;
    currentPath: string;
    onOpen: (path: string) => void;
    /** The caller's own personal folder, labelled "My notes". */
    personalFolder?: string;
    /** Shown on folders the caller may add notes to. */
    onCreate?: (folder: string) => void;
    /** IT only: shown on every folder except personal space, to set who can see it. */
    onFolderAccess?: (folder: string) => void;
}

/** Folders that hold the current note, so the tree opens to it. */
const ancestorsOf = (path: string): string[] => {
    const parts = path.split('/');
    return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
};

/** Folder names like "01-orientation" read better without the sort prefix. */
const folderLabel = (name: string): string => name.replace(/^\d{1,3}[-_ ]+/, '');

/**
 * The vault's folders and the notes the signed-in user may read. Folders with nothing readable never
 * arrive from the Hub, so this shows the tree as given.
 */
const KnowledgeTreeView = (props: Props): JSX.Element => {
    const [open, setOpen] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (props.currentPath) {
            setOpen(previous => new Set([...Array.from(previous), ...ancestorsOf(props.currentPath)]));
        }
    }, [props.currentPath]);

    const toggle = (path: string) => {
        setOpen(previous => {
            const next = new Set(previous);
            if (next.has(path)) {
                next.delete(path);
            }
            else {
                next.add(path);
            }
            return next;
        });
    };

    const renderFolder = (folder: TreeFolder, depth: number): JSX.Element => (
        <List disablePadding dense key={folder.path || '(root)'}>
            {folder.folders.map(child => {
                const isOpen = open.has(child.path);
                return (
                    <React.Fragment key={child.path}>
                        <ListItemButton sx={{ pl: 1 + depth * 2 }} onClick={() => toggle(child.path)}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {isOpen ? <FolderOpen fontSize="small" color="action" /> : <Folder fontSize="small" color="action" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={child.path === props.personalFolder ? 'My notes' : folderLabel(child.name)}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                            {child.canCreate && props.onCreate &&
                                <Tooltip title="New note here">
                                    <IconButton size="small" edge="end" sx={{ mr: 0.5 }} aria-label="New note here"
                                        onClick={(e) => { e.stopPropagation(); props.onCreate!(child.path); }}>
                                        <Add fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            }
                            {props.onFolderAccess && !child.path.toLowerCase().startsWith('personal') &&
                                <Tooltip title="Who can see this folder">
                                    <IconButton size="small" edge="end" sx={{ mr: 0.5 }} aria-label="Who can see this folder"
                                        onClick={(e) => { e.stopPropagation(); props.onFolderAccess!(child.path); }}>
                                        <LockPersonOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            }
                            {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </ListItemButton>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            {renderFolder(child, depth + 1)}
                        </Collapse>
                    </React.Fragment>
                );
            })}
            {folder.notes.map(note => (
                <ListItemButton
                    key={note.path}
                    sx={{ pl: 1 + depth * 2 }}
                    selected={note.path === props.currentPath}
                    onClick={() => props.onOpen(note.path)}
                >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                        <ArticleOutlined fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primary={note.title} title={note.title} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                </ListItemButton>
            ))}
        </List>
    );

    return renderFolder(props.root, 0);
};

export default KnowledgeTreeView;
